import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { verifyMagicLink } from '../services/api';
import { useTheme } from '../context/ThemeContext';
// import ThemeToggle from '../components/ThemeToggle'; // dark mode disabled for now
import { Loader2 } from 'lucide-react';

const VerifyMagicLinkPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDark } = useTheme();
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(true);

  const token = searchParams.get('token');
  // Redirect is base64url-encoded to survive ct.turing.com click tracker (which strips % chars)
  const rawParam = searchParams.get('redirect') || '';
  let rawRedirect = '/';
  try {
    rawRedirect = atob(rawParam.replace(/-/g, '+').replace(/_/g, '/')) || '/';
  } catch {
    // Fallback: treat as plain path (backward compat with old non-encoded links)
    rawRedirect = rawParam || '/';
  }
  // Only allow same-origin paths to prevent open redirect
  const redirect =
    rawRedirect.startsWith('/') && !rawRedirect.startsWith('//')
      ? rawRedirect
      : '/';

  useEffect(() => {
    if (!token) {
      setError('No sign-in token found in the URL.');
      setVerifying(false);
      return;
    }

    let cancelled = false;

    const verify = async () => {
      try {
        await verifyMagicLink(token);
        if (!cancelled) {
          navigate(redirect, { replace: true });
        }
      } catch (err: any) {
        if (!cancelled) {
          const message =
            err?.response?.data?.message ||
            err?.message ||
            'This sign-in link is invalid or has expired.';
          setError(message);
          setVerifying(false);
        }
      }
    };

    verify();

    return () => {
      cancelled = true;
    };
  }, [token, navigate, redirect]);

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-background flex flex-col items-center justify-center px-4 relative">
      {/* Dark mode disabled for now
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>
      */}
      <div className="h-6 mb-8">
        <img
          src={isDark ? '/turing-logo-white.svg' : '/turing-logo.svg'}
          alt="Turing"
          className="h-full object-contain"
        />
      </div>

      <div className="w-full max-w-sm">
        <div className="bg-white dark:bg-card border border-zinc-200 dark:border-border shadow-md rounded-2xl p-6 sm:p-8 text-center">
          {verifying ? (
            <>
              <div className="w-14 h-14 bg-zinc-100 dark:bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-6 h-6 text-zinc-600 dark:text-zinc-400 animate-spin" />
              </div>
              <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
                Signing you in...
              </h2>
              <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
                Please wait while we verify your sign-in link.
              </p>
            </>
          ) : error ? (
            <>
              <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
                Sign-in failed
              </h2>
              <div className="mt-4 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800/50 px-4 py-3 text-sm text-red-700 dark:text-red-400">
                {error}
              </div>
              <button
                onClick={() => navigate('/login', { replace: true })}
                className="mt-6 text-sm underline text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors"
              >
                Request a new sign-in link
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default VerifyMagicLinkPage;
